import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { reduxForm, Field, FieldArray, FormSection, formValueSelector, autofill } from 'redux-form';
import { Modal, Button, ButtonToolbar, Row, Col, ButtonGroup } from 'react-bootstrap';

import { RenderField, RenderSelect } from 'shared/render-field';
import { findItemById } from 'helpers/ListHelpers';
import { addMaterial, removeMaterial, changeMaterialBrand } from 'actions/brand-series-actions';
import { updateSaleMaterials, openMaterialsEditModal, closeMaterialsEditModal } from 'actions/materials-actions';

const MATERIALS_EDIT_FORM_NAME = 'materialsEditForm';

class MaterialsEditModal extends React.Component {
  constructor(props) {
    super(props);
    this.getMaterialsBrandSeries = this.getMaterialsBrandSeries.bind(this);
    this.getBrandSeries = this.getBrandSeries.bind(this);
    this.addMaterialFields = this.addMaterialFields.bind(this);
    this.removeMaterialFields = this.removeMaterialFields.bind(this);
    this.onBrandSelect = this.onBrandSelect.bind(this);
    this.renderMaterials = this.renderMaterials.bind(this);
    this.renderAddMaterialButtons = this.renderAddMaterialButtons.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.initializeBrandSeries = this.initializeBrandSeries.bind(this);
  }
  
  componentDidMount() {
    // Initialize brand series dropdowns for existing materials
    this.initializeBrandSeries();
  }
  
  componentDidUpdate(prevProps) {
    // If modal is newly opened or materials data changed
    if ((!prevProps.show && this.props.show) || 
        (prevProps.soldDetails !== this.props.soldDetails)) {
      this.initializeBrandSeries();
    }
  }
  
  initializeBrandSeries() {
    const { materialBrands, dispatch } = this.props;
    const materials = (this.props.initialValues && this.props.initialValues.materials) || [];
    const materialsBrandSeries = this.getMaterialsBrandSeries();
    
    // Initialize brand series options for each material that has a brand selected
    materials.forEach((material, index) => {
      if (material.brand) {
        const brand = findItemById(materialBrands || [], material.brand);
        if (brand && brand.brand_items) {
          const brandSeries = [...brand.brand_items];
          materialsBrandSeries[index] = brandSeries;
        }
      }
    });
    
    dispatch(changeMaterialBrand(materialsBrandSeries));
  }

  getMaterialsBrandSeries() {
    const { materialsBrandSeries } = this.props;
    return Array.isArray(materialsBrandSeries) ? [...materialsBrandSeries] : [];
  }

  getBrandSeries(index) {
    const series = this.getMaterialsBrandSeries();
    return series && Array.isArray(series) && series[index] ? series[index] : [];
  }

  addMaterialFields(fields) {
    const { dispatch } = this.props;
    dispatch(addMaterial());
    fields.push({});
  }

  removeMaterialFields(fields, index) {
    const { dispatch } = this.props;
    dispatch(removeMaterial(index));
    fields.remove(index);
  }

  onBrandSelect(value, index) {
    const { materialBrands, dispatch } = this.props;
    dispatch(autofill(MATERIALS_EDIT_FORM_NAME, `materials[${index}].brand_series`, null));
    const materialsBrandSeries = this.getMaterialsBrandSeries();
    const brand = findItemById(materialBrands, value);
    const brandSeries = value ? [...brand.brand_items] : [];
    materialsBrandSeries.splice(index, 1, brandSeries);
    dispatch(changeMaterialBrand(materialsBrandSeries));
  }

  renderAddMaterialButtons(fields, index) {
    const lastMaterial = index === (fields.length - 1);
    return (
      <Col md={12}>
        <ButtonToolbar>
          <ButtonGroup bsSize="small" className="pull-right">
            {lastMaterial && <Button onClick={() => this.addMaterialFields(fields)}>Add more</Button>}
            {(fields.length > 1) && <Button onClick={() => this.removeMaterialFields(fields, index)}>Remove</Button>}
          </ButtonGroup>
        </ButtonToolbar>
      </Col>
    );
  }

  renderMaterials({ fields }) {
    // Ensure materialBrands is always an array, even if undefined
    const materialBrands = this.props.materialBrands || [];
    return fields.map((material, index) => {
      // Ensure brandSeries is always an array, even if empty
      let brandSeries = this.getBrandSeries(index) || [];
      return (
        <Row className="clearfix" key={index}>
          <FormSection name={material}>
            <Col md={4}>
              <Field
                name="brand"
                label="Brand"
                option="Select Brand"
                component={RenderSelect}
                options={materialBrands}
                onChange={(e, value) => this.onBrandSelect(value, index)}
              />
            </Col>
            <Col md={4}>
              <Field
                name="brand_series"
                label="Brand Series"
                option="Select Series"
                component={RenderSelect}
                options={brandSeries}
              />
            </Col>
            <Col md={4}>
              <Field 
                name="color" 
                label="Color" 
                component={RenderField} 
              />
            </Col>
            {this.renderAddMaterialButtons(fields, index)}
          </FormSection>
        </Row>
      );
    });
  }

  handleSubmit(values) {
    const { onSubmit, soldDetails } = this.props;
    
    const data = {
      id: this.props.id,
      materials: values.materials
    };
    onSubmit(data);
  }

  render() {
    const { show, close, handleSubmit, submitting, soldDetails } = this.props;

    if (!soldDetails) return null;

    return (
      <Modal show={show} onHide={close} bsSize="large">
        <Modal.Header closeButton>
          <Modal.Title>Edit Materials</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit(this.handleSubmit)}>
          <Modal.Body>
            <FieldArray name="materials" component={this.renderMaterials} />
          </Modal.Body>
          <Modal.Footer>
            <ButtonToolbar className="pull-right">
              <Button onClick={close}>Cancel</Button>
              <Button bsStyle="primary" type="submit" disabled={submitting}>Save Changes</Button>
            </ButtonToolbar>
          </Modal.Footer>
        </form>
      </Modal>
    );
  }
}

MaterialsEditModal.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  soldDetails: PropTypes.object,
  materialBrands: PropTypes.array,
  materialsBrandSeries: PropTypes.array
};

const selector = formValueSelector(MATERIALS_EDIT_FORM_NAME);

MaterialsEditModal = reduxForm({
  form: MATERIALS_EDIT_FORM_NAME,
  enableReinitialize: true
})(MaterialsEditModal);

MaterialsEditModal = connect(
  state => ({
    initialValues: {
      materials: state.materials.data.materials || []
    }
  })
)(MaterialsEditModal);

export default MaterialsEditModal;
